import { useEffect, useMemo, useState } from 'react';

import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  Input,
  InputGroup,
  MultipleChoice,
  Select,
  ToggleSwitch,
  ToggleSwitchInput,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import styles from './RenewalConfigModal.module.scss';

export const RenewalConfigModal = ({
  companyUsers,
  brands,
  renewalConfig,
  companyChecks,
  visible,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const [checkType, setCheckType] = useState();
  const [companyCheck, setCompanyCheck] = useState();

  const { t } = useTranslation();

  useEffect(() => {
    if (!renewalConfig) {
      setCompanyCheck(null);
      setCheckType(null);
      return;
    }
    setCheckType(renewalConfig.type);
  }, [renewalConfig]);

  useEffect(() => {
    if (!checkType) {
      setCompanyCheck(null);
      return;
    }
    setCompanyCheck(companyChecks.find(cc => cc.type === checkType));
  }, [checkType]);

  const handleSubmit = data => {
    setCheckType(false);
    onSubmit(data);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={
        renewalConfig
          ? t('renewals.tabs.configuration.edit_renewal')
          : t('renewals.tabs.configuration.add_renewal')
      }
      description={
        companyCheck
          ? companyCheck.check_type?.title
          : 'Select a check type to create a new renewal configuration.'
      }
      className="u-margin-y--large"
      small={true}>
      {checkType && companyCheck ? (
        <RenewalConfigForm
          renewalConfig={renewalConfig}
          companyCheck={companyCheck}
          companyUsers={companyUsers}
          brands={brands}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      ) : (
        <TypeSelector companyChecks={companyChecks} onSelect={setCheckType} onClose={onClose} />
      )}
    </AnimatedModal>
  );
};

const TypeSelector = ({ companyChecks, onSelect = () => {} }) => {
  const [selectedType, setSelectedType] = useState(null);

  return (
    <div className="u-margin-top">
      {companyChecks.map(companyCheck => (
        <div
          key={companyCheck.type}
          className="card card-with-border d-flex align-items-center justify-content-between u-padding u-margin-top u-pointer"
          onClick={() => setSelectedType(companyCheck.type)}>
          <div className="d-flex align-items-center">
            <IconComponent
              icon={getCheckTypeIcon(companyCheck.type)}
              className="u-margin-right--small"
            />
            <p className="title-4">{companyCheck.check_type.title}</p>
          </div>
          <span
            className={cn(styles.radioButton, {
              [styles.radioButtonSelected]: companyCheck.type === selectedType,
            })}></span>
        </div>
      ))}
      <div className="d-flex justify-content-end">
        <Button
          className="u-margin-top--large"
          type="primary"
          onClick={() => onSelect(selectedType)}>
          Continue
        </Button>
      </div>
    </div>
  );
};

const OPERATOR_NO_VALUE = ['exists', 'not_exists', 'is_empty', 'not_empty'];

const EMPTY_CONDITION = {
  condition_input_source: '',
  condition_input_field: '',
  condition_operator: '',
  condition_value: '',
};

const RenewalConfigForm = ({
  renewalConfig,
  companyCheck,
  companyUsers = [],
  brands = [],
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();

  const { register, watch, reset, control, setValue, handleSubmit } = useForm({
    defaultValues: {
      name: renewalConfig?.name || companyCheck?.check_type?.title,
      renewal_mode: renewalConfig?.renewal_mode || 'use_expiration',
      frequency: renewalConfig?.frequency || 365,
      days_before_expiration: renewalConfig?.days_before_expiration || 0,
      renew_on_expiration: renewalConfig?.renew_on_expiration || false,
      contact_employee: renewalConfig?.contact_employee ? renewalConfig?.contact_employee : true,
      auto_assign: renewalConfig?.auto_assign || false,
      auto_assign_type: renewalConfig?.auto_assign_type || 'future_only',
    },
  });

  const [useCustomBrand, setUseCustomBrand] = useState(false);
  const [useAutoAssign, setUseAutoAssign] = useState(false);
  const [useCustomUser, setUseCustomUser] = useState(false);
  const [conditions, setConditions] = useState([{ ...EMPTY_CONDITION }]);
  const [connector, setConnector] = useState('and');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!renewalConfig) {
      return;
    }

    if (renewalConfig.brand_id) {
      setUseCustomBrand(true);
    }
    if (renewalConfig.user_id) {
      setUseCustomUser(true);
    }
    if (renewalConfig.auto_assign) {
      setUseAutoAssign(true);
    }
    if (renewalConfig.renewal_config_conditions?.length > 0) {
      setConditions(renewalConfig.renewal_config_conditions);
      setConnector(renewalConfig.renewal_config_conditions[0]?.connector ?? 'and');
    }

    reset(renewalConfig);
  }, [renewalConfig]);

  const renewalModeOptions = useMemo(() => {
    let options = [];

    if (companyCheck.check_type.has_expiration_date) {
      options.push({
        name: t('renewals.tabs.configuration.use_expiration'),
        value: 'use_expiration',
        description: t('renewals.tabs.configuration.use_expiration_description'),
      });
    }

    options.push({
      name: t('renewals.tabs.configuration.use_fixed_cadence'),
      value: 'fixed',
      description: t('renewals.tabs.configuration.use_fixed_cadence_description'),
    });

    options.push({
      name: t('renewals.tabs.configuration.conditional_renewal'),
      value: 'conditional',
      description: t('renewals.tabs.configuration.conditional_renewal_description'),
    });

    return { options };
  }, [companyCheck]);

  const addCondition = () => {
    setConditions(prev => [...prev, { ...EMPTY_CONDITION }]);
  };

  const removeCondition = index => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  const updateCondition = (index, field, value) => {
    setConditions(prev => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const getConditionError = (condition, field) => {
    if (!submitted) return '';
    if (field === 'condition_value') {
      return !OPERATOR_NO_VALUE.includes(condition.condition_operator) && !condition.condition_value
        ? t('validation.required')
        : '';
    }
    return !condition[field] ? t('validation.required') : '';
  };

  const handleFormSubmit = data => {
    if (data.renewal_mode === 'conditional') {
      setSubmitted(true);
      const invalid = conditions.some(
        c =>
          !c.condition_input_source ||
          !c.condition_input_field ||
          !c.condition_operator ||
          (!OPERATOR_NO_VALUE.includes(c.condition_operator) && !c.condition_value),
      );
      if (invalid) return;
      onSubmit({ ...data, renewal_config_conditions: conditions.map(c => ({ ...c, connector })) });
    } else {
      onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <input hidden {...register('type')} value={companyCheck.type} />
      <input hidden {...register('company_id')} value={companyCheck.company_id} />
      <InputGroup title={t('renewals.tabs.configuration.renewal_name')}>
        <Input name="name" register={register} />
      </InputGroup>
      <ToggleSwitchInput
        text={t('renewals.tabs.configuration.use_custom_brand')}
        subtext={t('renewals.tabs.configuration.use_custom_brand_description')}
        onChange={e => {
          setUseCustomBrand(e.target.checked);
          if (!e.target.checked) {
            setValue('brand_id', null);
          }
        }}
        value={useCustomBrand}
        className="u-width-100 u-margin-bottom">
        {useCustomBrand && (
          <Select name="brand_id" register={register} value={watch('brand_id')} useDefault>
            {brands.map(brand => (
              <Select.Item key={brand.id} value={brand.id}>
                {brand.name}
              </Select.Item>
            ))}
          </Select>
        )}
      </ToggleSwitchInput>
      <ToggleSwitchInput
        text={t('renewals.tabs.configuration.use_custom_user')}
        subtext={t('renewals.tabs.configuration.use_custom_user_description')}
        onChange={e => {
          setUseCustomUser(e.target.checked);
          if (!e.target.checked) {
            setValue('user_id', null);
          }
        }}
        value={useCustomUser}
        className="u-width-100 u-margin-bottom">
        {useCustomUser && (
          <Select name="user_id" register={register} value={watch('user_id')} useDefault>
            {companyUsers.map(user => (
              <Select.Item key={user.id} value={user.id}>
                {user.first_name} {user.last_name} • {user.email}
              </Select.Item>
            ))}
          </Select>
        )}
      </ToggleSwitchInput>

      <ToggleSwitchInput
        text={t('renewals.tabs.configuration.auto_assign')}
        subtext={t('renewals.tabs.configuration.auto_assign_description')}
        onChange={e => {
          setUseAutoAssign(e.target.checked);
          setValue('auto_assign', e.target.checked || null);
        }}
        value={useAutoAssign}
        className="u-width-100 u-margin-bottom">
        {useAutoAssign && (
          <Controller
            name="auto_assign_type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultipleChoice
                value={value}
                onChange={onChange}
                defaultValue="future_only"
                disabled={!!renewalConfig?.id}
                smallMargin
                radioButtonStyle
                noLabel
                options={{
                  options: [
                    { value: 'future_only', name: 'Future only' },
                    { value: 'existing_and_future', name: 'Existing + Future' },
                  ],
                }}
              />
            )}
          />
        )}
      </ToggleSwitchInput>

      <ToggleSwitch
        name="auto_renew"
        register={register}
        className={styles.compactToggleSwitch}
        checked={watch('auto_renew')}
        tooltip={t('renewals.tabs.configuration.auto_renewal_description')}
        label={t('renewals.tabs.configuration.auto_renewal')}
      />

      <ToggleSwitch
        name="contact_employee"
        register={register}
        className={styles.compactToggleSwitch}
        disabled={companyCheck?.check_type?.require_renewal_consent}
        checked={watch('contact_employee')}
        tooltip={t('renewals.tabs.configuration.contact_employee_description')}
        label={t('renewals.tabs.configuration.contact_employee')}
      />

      <InputGroup title={t('renewals.tabs.configuration.renewal_mode')}>
        <Controller
          name="renewal_mode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultipleChoice
              value={value}
              onChange={onChange}
              radioButtonStyle
              noLabel
              options={renewalModeOptions}
            />
          )}
        />
      </InputGroup>

      {watch('renewal_mode') !== 'use_expiration' ? (
        <div className="card card-with-border u-padding">
          {watch('renewal_mode') === 'conditional' && (
            <>
              <InputGroup title={t('renewals.tabs.configuration.conditions')}>
                {conditions.length > 1 && (
                  <div className={styles.conditionConnector}>
                    <Button
                      type={connector !== 'or' ? 'primary' : 'secondary'}
                      small
                      onClick={() => setConnector('and')}>
                      {t('renewals.tabs.configuration.connector_and')}
                    </Button>
                    <Button
                      type={connector === 'or' ? 'primary' : 'secondary'}
                      small
                      onClick={() => setConnector('or')}>
                      {t('renewals.tabs.configuration.connector_or')}
                    </Button>
                  </div>
                )}
                {conditions.map((condition, index) => (
                  <div key={index}>
                    {index > 0 && (
                      <div className={styles.conditionConnector}>
                        <span className="t-small">
                          {connector === 'or'
                            ? t('renewals.tabs.configuration.connector_or')
                            : t('renewals.tabs.configuration.connector_and')}
                        </span>
                      </div>
                    )}
                    <div className={styles.conditionRow}>
                      {conditions.length > 1 && (
                        <span
                          className={styles.removeCondition}
                          onClick={() => removeCondition(index)}>
                          <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2} />
                        </span>
                      )}
                      <InputGroup title={t('renewals.tabs.configuration.input_source')}>
                        <Select
                          value={condition.condition_input_source}
                          onChange={e =>
                            updateCondition(index, 'condition_input_source', e.target.value)
                          }
                          error={getConditionError(condition, 'condition_input_source')}
                          useDefault>
                          <Select.Item value="candidate_checks.details">
                            {t(
                              'renewals.tabs.configuration.input_source_options.launching_details',
                            )}
                          </Select.Item>
                          <Select.Item value="candidates_checks_values.values">
                            {t(
                              'renewals.tabs.configuration.input_source_options.candidate_details',
                            )}
                          </Select.Item>
                          <Select.Item value="candidate_checks.extra_data">
                            {t('renewals.tabs.configuration.input_source_options.result_values')}
                          </Select.Item>
                        </Select>
                      </InputGroup>
                      <InputGroup title={t('renewals.tabs.configuration.input_field')}>
                        <Input
                          error={getConditionError(condition, 'condition_input_field')}
                          inputProps={{
                            value: condition.condition_input_field,
                            onChange: e =>
                              updateCondition(index, 'condition_input_field', e.target.value),
                          }}
                        />
                      </InputGroup>
                      <InputGroup title={t('renewals.tabs.configuration.operator')}>
                        <Select
                          value={condition.condition_operator}
                          onChange={e =>
                            updateCondition(index, 'condition_operator', e.target.value)
                          }
                          error={getConditionError(condition, 'condition_operator')}
                          useDefault>
                          <Select.Item value="exists">
                            {t('renewals.tabs.configuration.operator_options.exists')}
                          </Select.Item>
                          <Select.Item value="not_exists">
                            {t('renewals.tabs.configuration.operator_options.not_exists')}
                          </Select.Item>
                          <Select.Item value="is">
                            {t('renewals.tabs.configuration.operator_options.is')}
                          </Select.Item>
                          <Select.Item value="is_not">
                            {t('renewals.tabs.configuration.operator_options.is_not')}
                          </Select.Item>
                          <Select.Item value="in">
                            {t('renewals.tabs.configuration.operator_options.in')}
                          </Select.Item>
                          <Select.Item value="not_in">
                            {t('renewals.tabs.configuration.operator_options.not_in')}
                          </Select.Item>
                          <Select.Item value="contains">
                            {t('renewals.tabs.configuration.operator_options.contains')}
                          </Select.Item>
                          <Select.Item value="not_contains">
                            {t('renewals.tabs.configuration.operator_options.not_contains')}
                          </Select.Item>
                          <Select.Item value="not_empty">
                            {t('renewals.tabs.configuration.operator_options.not_empty')}
                          </Select.Item>
                          <Select.Item value="is_empty">
                            {t('renewals.tabs.configuration.operator_options.is_empty')}
                          </Select.Item>
                          <Select.Item value="less_than">
                            {t('renewals.tabs.configuration.operator_options.less_than')}
                          </Select.Item>
                          <Select.Item value="greater_than">
                            {t('renewals.tabs.configuration.operator_options.greater_than')}
                          </Select.Item>
                        </Select>
                      </InputGroup>
                      {!OPERATOR_NO_VALUE.includes(condition.condition_operator) && (
                        <InputGroup title={t('renewals.tabs.configuration.value')}>
                          <Input
                            error={getConditionError(condition, 'condition_value')}
                            inputProps={{
                              value: condition.condition_value || '',
                              onChange: e =>
                                updateCondition(index, 'condition_value', e.target.value),
                            }}
                          />
                        </InputGroup>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  type="secondary"
                  small
                  className="u-margin-top--small"
                  onClick={addCondition}>
                  {t('renewals.tabs.configuration.add_condition')}
                </Button>
              </InputGroup>
            </>
          )}

          <InputGroup title="Frequency (days)">
            <Input name="frequency" register={register} type="number" min={0} />
          </InputGroup>
          {companyCheck.check_type.has_expiration_date && (
            <ToggleSwitch
              name="renew_on_expiration"
              register={register}
              className="u-margin-bottom--0"
              checked={watch('renew_on_expiration')}
              tooltip={t('renewals.tabs.configuration.renew_on_expiration_description')}
              label={t('renewals.tabs.configuration.renew_on_expiration')}
            />
          )}
        </div>
      ) : (
        <>
          <InputGroup
            title="Days before expiration"
            tooltip="How many days before the expiration date should the renewal be triggered?">
            <Input name="days_before_expiration" register={register} type="number" />
          </InputGroup>
        </>
      )}

      <div className="d-flex justify-content-end u-margin-top--large">
        <Button type="secondary" onClick={onClose} className="u-margin-right">
          Cancel
        </Button>
        <Button type="primary" submit>
          Save Renewal
        </Button>
      </div>
    </form>
  );
};
